import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MediaFile } from '../types/ServiceRequest';
import { useUser } from '../contexts/UserContext';

// ServiceRequest'teki MediaFileWithStatus ile uyumlu interface
interface MediaFileWithStatus extends MediaFile {
  uploadStatus?: 'uploading' | 'success' | 'error';
  existingFileId?: number;
  existingFileRumuz?: string;
  streamUrl?: string; // Video stream için ayrı URL
}

interface MediaUploadStepProps {
  mediaFiles: MediaFileWithStatus[];
  uploadingFiles: boolean;
  isUploading: boolean;
  mediaUploadError: string;
  onBackToPartSelection: () => void;
  onMediaFilesUpdate: (files: MediaFileWithStatus[]) => void;
  onMediaUploadErrorUpdate: (error: string) => void;
  onCreateRequest: () => void;
  isCheckingWarrantyParts?: boolean;
}

const MediaUploadStep: React.FC<MediaUploadStepProps> = ({
  mediaFiles,
  uploadingFiles,
  isUploading,
  mediaUploadError,
  onBackToPartSelection,
  onMediaFilesUpdate,
  onMediaUploadErrorUpdate,
  onCreateRequest,
  isCheckingWarrantyParts = false
}) => {
  const { userData } = useUser();
  const configuredMin = userData?.MinImageCountOnWarranty ?? 0;
  const configuredMax = userData?.MaxImageCountOnWarranty ?? 0;
  const minImageCount = configuredMin > 0
    ? configuredMin
    : (userData?.ImageRequiredOnWarranty ? 1 : 0);
  const maxImageCount = configuredMax > 0 ? configuredMax : 0;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  // Drag & Drop state'leri
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const dragImageCounterRef = useRef(0);
  const dragVideoCounterRef = useRef(0);

  // Medya dosyalarını filtreleme
  const imageFiles = mediaFiles.filter(file => file.type === 'image');
  const videoFile = mediaFiles.find(file => file.type === 'video');
  const imageCount = imageFiles.length;
  const canAddMoreImages = maxImageCount === 0 || imageCount < maxImageCount;
  const canAddVideo = !videoFile;
  const hasMinimumRequiredImages = imageCount >= minImageCount;

  const imageLimitHint = minImageCount > 0 && maxImageCount > 0
    ? `En az ${minImageCount}, en fazla ${maxImageCount} görsel yükleyebilirsiniz. `
    : minImageCount > 0
      ? `En az ${minImageCount} görsel yüklemelisiniz. `
      : maxImageCount > 0
        ? `En fazla ${maxImageCount} görsel yükleyebilirsiniz. `
        : '';

  const exceedsImageLimit = (currentCount: number, incomingCount: number) =>
    maxImageCount > 0 && currentCount + incomingCount > maxImageCount;

  // Yükleme durumunu body attribute'u ile işaretle
  useEffect(() => {
    if (uploadingFiles || isUploading) {
      document.body.setAttribute('data-uploading', 'true');
    } else {
      document.body.removeAttribute('data-uploading');
    }

    // Cleanup function
    return () => {
      document.body.removeAttribute('data-uploading');
    };
  }, [uploadingFiles, isUploading]);

  // Dosya benzersizliği kontrol fonksiyonu
  const getFileIdentifier = (file: File): string => {
    return `${file.name}-${file.size}-${file.lastModified}-${file.type}`;
  };

  const isFileAlreadyAdded = (file: File): boolean => {
    const newFileId = getFileIdentifier(file);
    return mediaFiles.some(existingFile => {
      if (existingFile.file) {
        const existingFileId = getFileIdentifier(existingFile.file);
        return existingFileId === newFileId;
      }
      return false;
    });
  };

  // Görsel dosyaları yükleme
  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    // Input değerini hata durumunda sıfırlamak için yardımcı fonksiyon
    const resetInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const currentImageCount = mediaFiles.filter(file => file.type === 'image').length;
    const files = Array.from(e.target.files);
    const selectedImages = files.filter(file => file.type.startsWith('image/'));

    if (selectedImages.length !== files.length) {
      onMediaUploadErrorUpdate('Sadece görsel dosyaları yükleyebilirsiniz.');
      resetInput();
      return;
    }

    if (exceedsImageLimit(currentImageCount, selectedImages.length)) {
      onMediaUploadErrorUpdate(`En fazla ${maxImageCount} görsel yükleyebilirsiniz.`);
      resetInput();
      return;
    }

    const newFiles: MediaFileWithStatus[] = [];
    const duplicateFiles: string[] = [];

    for (let i = 0; i < selectedImages.length; i++) {
      const file = selectedImages[i];

      // Dosya boyutu kontrolü (25 MB)
      if (file.size > 25 * 1024 * 1024) {
        onMediaUploadErrorUpdate('Görsel dosyaları en fazla 25 MB olabilir.');
        resetInput();
        return;
      }

      // Tekrarlanan dosya kontrolü
      if (isFileAlreadyAdded(file)) {
        duplicateFiles.push(file.name);
        continue;
      }

      // Önizleme URL'i oluştur
      const preview = URL.createObjectURL(file);

      newFiles.push({
        id: uuidv4(),
        file,
        preview,
        type: 'image',
        uploadStatus: undefined
      } as MediaFileWithStatus);
    }

    // Tekrarlanan dosya uyarısı
    if (duplicateFiles.length > 0) {
      const duplicateMessage = duplicateFiles.length === 1 
        ? `"${duplicateFiles[0]}" dosyası zaten eklenmiş.`
        : `${duplicateFiles.length} dosya zaten eklenmiş: ${duplicateFiles.slice(0, 3).join(', ')}${duplicateFiles.length > 3 ? '...' : ''}`;
      onMediaUploadErrorUpdate(duplicateMessage);
      resetInput();
      return;
    }

    // Yeni dosyaları mevcut dosyalara ekle
    if (newFiles.length > 0) {
      onMediaFilesUpdate([...mediaFiles, ...newFiles]);
      onMediaUploadErrorUpdate(''); // Hata mesajını temizle
    }
    
    // Her durumda input'u sıfırla
    resetInput();
  };

  // Video dosyası yükleme
  const handleVideoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    // Input değerini hata durumunda sıfırlamak için yardımcı fonksiyon
    const resetInput = () => {
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    };

    // Zaten bir video yüklenmiş mi kontrol et
    const hasVideo = mediaFiles.some(file => file.type === 'video');

    if (hasVideo) {
      onMediaUploadErrorUpdate('Sadece bir video yükleyebilirsiniz.');
      resetInput();
      return;
    }

    const file = e.target.files[0];

    // Dosya tipi kontrolü
    if (!file.type.startsWith('video/')) {
      onMediaUploadErrorUpdate('Sadece video dosyaları yükleyebilirsiniz.');
      resetInput();
      return;
    }

    // Dosya boyutu kontrolü (100 MB)
    if (file.size > 100 * 1024 * 1024) {
      onMediaUploadErrorUpdate('Video dosyası en fazla 100 MB olabilir.');
      resetInput();
      return;
    }

    // Tekrarlanan dosya kontrolü
    if (isFileAlreadyAdded(file)) {
      onMediaUploadErrorUpdate(`"${file.name}" dosyası zaten eklenmiş.`);
      resetInput();
      return;
    }

    // Video dosyası için önizleme URL'i oluştur
    const preview = URL.createObjectURL(file);

    onMediaFilesUpdate([...mediaFiles, {
      id: uuidv4(),
      file,
      preview,
      type: 'video',
      uploadStatus: undefined
    } as MediaFileWithStatus]);

    onMediaUploadErrorUpdate(''); // Hata mesajını temizle
    // Input değerini sıfırla
    resetInput();
  };

  // Medya dosyası silme
  const handleRemoveMedia = (id: string) => {
    const fileToRemove = mediaFiles.find(file => file.id === id);

    if (fileToRemove) {
      // Mevcut dosyalar için URL.revokeObjectURL çağırma (API URL'leri için gerekli değil)
      if (!fileToRemove.existingFileId) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      // Dosyayı state'ten kaldır
      const newFiles = mediaFiles.filter(file => file.id !== id);
      onMediaFilesUpdate(newFiles);
    } else {
    }
  };

  // Görsel Drag & Drop handler'ları
  const handleImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMoreImages && !(uploadingFiles || isUploading)) {
      setIsDraggingImage(true);
    }
  };

  const handleImageDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMoreImages && !(uploadingFiles || isUploading)) {
      dragImageCounterRef.current++;
      setIsDraggingImage(true);
    }
  };

  const handleImageDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (canAddMoreImages && !(uploadingFiles || isUploading)) {
      dragImageCounterRef.current--;
      // Counter sıfır olduğunda state'i resetle
      if (dragImageCounterRef.current <= 0) {
        dragImageCounterRef.current = 0;
        setIsDraggingImage(false);
      }
    }
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragImageCounterRef.current = 0;
    setIsDraggingImage(false);

    if (!canAddMoreImages || uploadingFiles || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      onMediaUploadErrorUpdate('Sadece görsel dosyaları yükleyebilirsiniz.');
      return;
    }

    const currentImageCount = mediaFiles.filter(file => file.type === 'image').length;

    if (exceedsImageLimit(currentImageCount, imageFiles.length)) {
      onMediaUploadErrorUpdate(`En fazla ${maxImageCount} görsel yükleyebilirsiniz.`);
      return;
    }

    const newFiles: MediaFileWithStatus[] = [];
    const duplicateFiles: string[] = [];

    for (const file of imageFiles) {
      // Dosya boyutu kontrolü (25 MB)
      if (file.size > 25 * 1024 * 1024) {
        onMediaUploadErrorUpdate('Görsel dosyaları en fazla 25 MB olabilir.');
        return;
      }

      // Tekrarlanan dosya kontrolü
      if (isFileAlreadyAdded(file)) {
        duplicateFiles.push(file.name);
        continue;
      }

      // Önizleme URL'i oluştur
      const preview = URL.createObjectURL(file);

      newFiles.push({
        id: uuidv4(),
        file,
        preview,
        type: 'image',
        uploadStatus: undefined
      } as MediaFileWithStatus);
    }

    // Tekrarlanan dosya uyarısı
    if (duplicateFiles.length > 0) {
      const duplicateMessage = duplicateFiles.length === 1 
        ? `"${duplicateFiles[0]}" dosyası zaten eklenmiş.`
        : `${duplicateFiles.length} dosya zaten eklenmiş: ${duplicateFiles.slice(0, 3).join(', ')}${duplicateFiles.length > 3 ? '...' : ''}`;
      onMediaUploadErrorUpdate(duplicateMessage);
      return;
    }

    // Yeni dosyaları mevcut dosyalara ekle
    if (newFiles.length > 0) {
      onMediaFilesUpdate([...mediaFiles, ...newFiles]);
      onMediaUploadErrorUpdate(''); // Hata mesajını temizle
    }
  };

  // Video Drag & Drop handler'ları
  const handleVideoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddVideo && !(uploadingFiles || isUploading)) {
      setIsDraggingVideo(true);
    }
  };

  const handleVideoDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddVideo && !(uploadingFiles || isUploading)) {
      dragVideoCounterRef.current++;
      setIsDraggingVideo(true);
    }
  };

  const handleVideoDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (canAddVideo && !(uploadingFiles || isUploading)) {
      dragVideoCounterRef.current--;
      // Counter sıfır olduğunda state'i resetle
      if (dragVideoCounterRef.current <= 0) {
        dragVideoCounterRef.current = 0;
        setIsDraggingVideo(false);
      }
    }
  };

  const handleVideoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragVideoCounterRef.current = 0;
    setIsDraggingVideo(false);

    if (!canAddVideo || uploadingFiles || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    
    if (videoFiles.length === 0) {
      onMediaUploadErrorUpdate('Sadece video dosyaları yükleyebilirsiniz.');
      return;
    }

    // Zaten bir video yüklenmiş mi kontrol et
    const hasVideo = mediaFiles.some(file => file.type === 'video');

    if (hasVideo) {
      onMediaUploadErrorUpdate('Sadece bir video yükleyebilirsiniz.');
      return;
    }

    const file = videoFiles[0]; // Sadece ilk video dosyasını al

    // Dosya boyutu kontrolü (100 MB)
    if (file.size > 100 * 1024 * 1024) {
      onMediaUploadErrorUpdate('Video dosyası en fazla 100 MB olabilir.');
      return;
    }

    // Tekrarlanan dosya kontrolü
    if (isFileAlreadyAdded(file)) {
      onMediaUploadErrorUpdate(`"${file.name}" dosyası zaten eklenmiş.`);
      return;
    }

    // Video dosyası için önizleme URL'i oluştur
    const preview = URL.createObjectURL(file);

    onMediaFilesUpdate([...mediaFiles, {
      id: uuidv4(),
      file,
      preview,
      type: 'video',
      uploadStatus: undefined
    } as MediaFileWithStatus]);

    onMediaUploadErrorUpdate(''); // Hata mesajını temizle
  };

  // Global drag & drop olaylarını engelle
  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleGlobalDrop = (e: DragEvent) => {
      e.preventDefault();
    };

    // Window'a event listener'ları ekle
    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('drop', handleGlobalDrop);

    // Cleanup
    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  return (
    <div className="max-w-md mx-auto">
      <div className="flex justify-center mb-8">
        <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
          <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-medium text-gray-900 dark:text-white">Görsel ve Video Ekle</h2>
        <button
          onClick={onBackToPartSelection}
          disabled={uploadingFiles || isUploading}
          className={`text-sm flex items-center ${uploadingFiles || isUploading 
            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' 
            : 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'
          }`}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Geri Dön
        </button>
      </div>

      {mediaUploadError && (
        <div className="bg-red-50 dark:bg-red-900/50 border-l-4 border-red-500 p-4 rounded mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{mediaUploadError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 overflow-hidden shadow-sm">
        <div className="rounded-lg border border-white dark:border-gray-700 overflow-hidden">
        {/* Görseller Bölümü */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            Görseller <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({imageCount}{maxImageCount > 0 ? `/${maxImageCount}` : ''})</span>
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {/* Yüklenen görselleri göster */}
            {imageFiles.map(file => (
              <div key={file.id} className="relative group aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                <img
                  src={file.preview}
                  alt="Yüklenen görsel"
                  className="w-full h-full object-cover"
                />
                
                {/* Upload durumu overlay */}
                {file.uploadStatus === 'uploading' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                    <svg className="animate-spin w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                
                {file.uploadStatus === 'error' && (
                  <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center z-20">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                
                {file.uploadStatus === 'success' && (
                  <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center z-20 opacity-0 animate-pulse">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveMedia(file.id);
                  }}
                  disabled={uploadingFiles || isUploading}
                  className={`absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 z-10 shadow-md transition-opacity duration-200 ${
                    uploadingFiles || isUploading 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'md:opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-5"></div>
              </div>
            ))}

            {/* Görsel ekleme butonu */}
            {canAddMoreImages && !(uploadingFiles || isUploading) && (
              <label 
                className={`aspect-square flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 group ${
                  isDraggingImage 
                    ? 'border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 scale-105' 
                    : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-blue-50 hover:via-indigo-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:via-indigo-900/15 dark:hover:to-blue-800/20 hover:border-blue-500 dark:hover:border-blue-400'
                }`}
                onDragOver={handleImageDragOver}
                onDragEnter={handleImageDragEnter}
                onDragLeave={handleImageDragLeave}
                onDrop={handleImageDrop}
              >
                <svg className={`w-8 h-8 mb-2 transition-colors duration-200 ${
                  isDraggingImage 
                    ? 'text-blue-500 dark:text-blue-400' 
                    : 'text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className={`text-sm transition-colors duration-200 ${
                  isDraggingImage 
                    ? 'text-blue-700 dark:text-blue-300 font-medium' 
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-700 dark:group-hover:text-blue-300'
                }`}>
                  {isDraggingImage ? 'Bırakın' : 'Görsel Ekle'}
                </span>
                {!isDraggingImage && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 mt-1">veya sürükleyin</span>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  multiple={canAddMoreImages}
                  onChange={handleImageUpload}
                />
              </label>
            )}
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {imageLimitHint}Desteklenen formatlar: JPG, PNG, GIF. Maksimum dosya boyutu: 25 MB.
          </p>
        </div>

        {/* Video Bölümü */}
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
            Video <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({videoFile ? '1' : '0'}/1)</span>
          </h3>

          <div className="mb-4">
            {videoFile ? (
              <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden group">
                <video
                  src={videoFile.streamUrl || videoFile.preview}
                  poster={videoFile.existingFileRumuz ? videoFile.preview : undefined}
                  controls
                  controlsList="nodownload"
                  playsInline
                  className="w-full h-full"
                  preload="metadata"
                ></video>

                {/* Upload durumu overlay */}
                {videoFile.uploadStatus === 'uploading' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
                    <svg className="animate-spin w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}

                {videoFile.uploadStatus === 'error' && (
                  <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center z-20">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}

                {videoFile.uploadStatus === 'success' && (
                  <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center z-20 opacity-0 animate-pulse">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveMedia(videoFile.id);
                  }}
                  disabled={uploadingFiles || isUploading}
                  className={`absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 z-10 shadow-md transition-opacity duration-200 ${
                    uploadingFiles || isUploading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'md:opacity-0 group-hover:opacity-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"></div>
              </div>
            ) : (
              <label 
                className={`aspect-video flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 group ${
                  isDraggingVideo 
                    ? 'border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 scale-105' 
                    : 'border-gray-300 dark:border-gray-600 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-blue-50 hover:via-indigo-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:via-indigo-900/15 dark:hover:to-blue-800/20 hover:border-blue-500 dark:hover:border-blue-400'
                }`}
                onDragOver={handleVideoDragOver}
                onDragEnter={handleVideoDragEnter}
                onDragLeave={handleVideoDragLeave}
                onDrop={handleVideoDrop}
              >
                <svg className={`w-12 h-12 mb-3 transition-colors duration-200 ${
                  isDraggingVideo 
                    ? 'text-blue-500 dark:text-blue-400' 
                    : 'text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
                <span className={`text-base mb-1 transition-colors duration-200 ${
                  isDraggingVideo 
                    ? 'text-blue-700 dark:text-blue-300 font-medium' 
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-blue-700 dark:group-hover:text-blue-300'
                }`}>
                  {isDraggingVideo ? 'Video Bırakın' : 'Video Ekle'}
                </span>
                {!isDraggingVideo && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400">veya buraya sürükleyin</span>
                )}
                <input
                  ref={videoInputRef}
                  type="file"
                  className="hidden"
                  accept="video/*"
                  onChange={handleVideoUpload}
                />
              </label>
            )}
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            Sadece bir video yükleyebilirsiniz. Desteklenen formatlar: MP4, MOV, AVI. Maksimum dosya boyutu: 100 MB.
          </p>
        </div>
        </div>
      </div>

      <div className="mt-8">
        <button
          type="button"
          className={`
            w-full flex items-center justify-center px-6 py-3.5
            text-base font-semibold text-white
            bg-gradient-to-r from-blue-600 to-indigo-600
            hover:from-blue-700 hover:to-indigo-700
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            rounded-lg shadow-lg shadow-blue-500/25
            transform transition-all duration-200
            hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
            active:scale-[0.98]
            relative overflow-hidden
            before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700
            ${(uploadingFiles || isUploading || isCheckingWarrantyParts || !hasMinimumRequiredImages) ? 'opacity-70 cursor-not-allowed' : ''}
          `}
          disabled={uploadingFiles || isUploading || isCheckingWarrantyParts || !hasMinimumRequiredImages}
          onClick={() => {
            if (!hasMinimumRequiredImages) {
              onMediaUploadErrorUpdate(`Devam etmek için en az ${minImageCount} görsel yüklemelisiniz.`);
              return;
            }
            onCreateRequest();
          }}
        >
          {(uploadingFiles || isUploading) ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isUploading ? 'Dosyalar Yükleniyor...' : 'Yükleniyor...'}
            </div>
          ) : isCheckingWarrantyParts ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Yedek parçalar kontrol ediliyor...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              Talebi Oluştur
            </div>
          )}
        </button>
        {!hasMinimumRequiredImages && (
          <p className="text-sm text-red-600 dark:text-red-300 mt-2 text-center">
            Talep oluşturmak için en az {minImageCount} görsel yüklemelisiniz.
          </p>
        )}
      </div>
    </div>
  );
};

export default MediaUploadStep;
